import React, { useCallback, useMemo, useState } from 'react';
import { User } from '@livingdesign/icons';
import bannerImage from "../../assets/svg/Recipients_banner.svg";
import { useAppContext } from '../../contexts/Core';
import SavedFiltersDataTable from '../../components/v2/common/SavedFiltersDataTable/SavedFiltersDataTable';
import MessageDetailsFormProvider, { useMessageDetailsForm } from '../../contexts/MessageDetailsForm';
import RecipientFilterProvider, { useRecipientFilter } from '../../contexts/RecipientFilter';
import SavedFiltersManagementProvider, { useSavedFiltersManagement } from '../../contexts/SavedFiltersManagement';
import QA from '../../consts/QA';
import './Recipient.scss';
import CampaignProvider from '../../contexts/Campaign';
import { Breadcrumb, BreadcrumbItem, SkeletonText } from '@livingdesign/react';
import RecipientFilterOverlay from '../../components/v2/Campaign/CampaignEdit/EditLayout/RecipientSelection/RecipientFilter/RecipientFilterOverlay';
import DivisionListOverlay from '../../components/v2/Campaign/CampaignEdit/EditLayout/RecipientSelection/SavedRecipients/DivisionListOverlay/DivisionListOverlay';
import { ACTIONS } from '../../consts/CommonV2';
import SavedRecipientsCreateModal from '../../components/v2/Campaign/CampaignEdit/EditLayout/RecipientSelection/SavedRecipients/SavedRecipientsCreateModal';


export const QA_ID_PREFIX = `${QA.PAGES.RECIPIENT}`;

// Internal component that uses the SavedFiltersManagement context
function RecipientContent() {
  const { selectedTeam, savedFiltersLoading } = useAppContext();
  const {
    showDivisionOverlay,
    selectedFilterForDivision,
    handleViewRecipientFilter,
    handleEditRecipientFilter,
    handleCreateRecipientFilter,
    handleMoreActions,
    handleCloseDivisionList,
    handleNavigateToSelectedFilter,
    handleNavigateToSavedFilters,
    mode,
    handlePrevious,
    refreshSavedFilters,
  } = useSavedFiltersManagement();

  const { filterName, setFilterName, deleteFilter} = useRecipientFilter();
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilterCreateModal, setShowFilterCreateModal] = useState(false);
  const { showRecipientFilterOverlay, setShowRecipientFilterOverlay } = useMessageDetailsForm();  

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };
  const overlayNavigation = useMemo( () => (
        <>
            <Breadcrumb data-testid={`${QA_ID_PREFIX}-breadcrumb`} UNSAFE_style={{ cursor: "pointer" }}>
                <BreadcrumbItem data-testid={`${QA_ID_PREFIX}-breadcrumb-home`}>Home</BreadcrumbItem>
                <BreadcrumbItem
                    onClick={handleNavigateToSavedFilters}
                    data-testid={`${QA_ID_PREFIX}-breadcrumb-saved-recipients`}
                >
                    Saved Recipient Filters
                </BreadcrumbItem>
                { ( mode === ACTIONS.RECIPIENT_PAGE_FILTER_VIEW || mode === ACTIONS.RECIPIENT_PAGE_FILTER_EDIT ) &&
                  <BreadcrumbItem
                      onClick={handleNavigateToSelectedFilter}
                      data-testid={`${QA_ID_PREFIX}-breadcrumb-division-list`}
                  >
                    { selectedFilterForDivision?.name }
                  </BreadcrumbItem>
                }
                { mode === ACTIONS.RECIPIENT_PAGE_FILTER_CREATE && 
                  (
                    <BreadcrumbItem
                        onClick={() => {}}
                        data-testid={`${QA_ID_PREFIX}-breadcrumb-division-list`}
                    >
                      Recipient Filtering
                    </BreadcrumbItem>
                  )
                }  
               { mode === ACTIONS.RECIPIENT_PAGE_FILTER_EDIT && 
                  (
                    <BreadcrumbItem
                        onClick={() => {}}
                        data-testid={`${QA_ID_PREFIX}-breadcrumb-division-list`}
                    >
                      Edit Filters
                    </BreadcrumbItem>
                  )
                }   
            </Breadcrumb>
      </> ), [mode, selectedFilterForDivision] )
    

    const handleSuccessUpdate = useCallback( ( { filterName, rawFilter, recipientCount } ) => {
      handlePrevious({ filterName, rawFilter, recipientCount })
      setShowRecipientFilterOverlay(false);
    } , [])
  
  return (
    <>
    <div 
      className={`recipient-page ${showDivisionOverlay || showRecipientFilterOverlay ? 'recipient-page-hidden' : ''}`}
      data-testid={`${QA_ID_PREFIX}-main`}
    >
      {/* Hero Banner */}
      <div className="recipient-banner" data-testid={`${QA_ID_PREFIX}-banner`}>
        <div className="recipient-banner-content" data-testid={`${QA_ID_PREFIX}-banner-content`}>
          <div className="recipient-banner-text" data-testid={`${QA_ID_PREFIX}-banner-text`}>
            <h1 className="recipient-banner-title" data-testid={`${QA_ID_PREFIX}-banner-title`}>
              Saved Recipient Filters
            </h1>
            <p className="recipient-banner-description" data-testid={`${QA_ID_PREFIX}-banner-description`}>
              Create, edit, and manage contacts in one place - individually or as a team. 
              Streamline collaboration with shared access and a unified view to keep team members aligned.
            </p>
          </div>
          <div className="recipient-banner-img" data-testid={`${QA_ID_PREFIX}-banner-image-container`}>
            <img className='campaign-edit-banner-image' loading="lazy" src={bannerImage} data-testid={`${QA_ID_PREFIX}-banner-image`} />
          </div>
        </div>
      </div>

      {/* Header with Team Name */}
      <div className="recipient-header" data-testid={`${QA_ID_PREFIX}-header`}>
        <div className="recipient-header-title" data-testid={`${QA_ID_PREFIX}-header-title`}>
          {selectedTeam?.name || ''}
        </div>
      </div>

      { savedFiltersLoading ?
              (
                <SkeletonText lines={15} data-testid={`${QA_ID_PREFIX}-skeleton-text`} />
              ) :
              (
                <SavedFiltersDataTable 
                  searchTerm={searchTerm}
                  onSearchChange={handleSearchChange}
                  onMoreActions={handleMoreActions}
                  onView={handleViewRecipientFilter}
                  onEdit={handleEditRecipientFilter}
                  deleteFilter={deleteFilter}
                  searchPlaceholder="Search for list names, or authors"
                  qaPrefix={QA_ID_PREFIX}
                  itemsPerPageDefault={10}
                  showCreateButton={true}
                  createButtonText="Create recipient filter"
                  createButtonIcon={<User />}
                  mode={mode}
                  onCreateClick={()=>{
                    setShowFilterCreateModal(true);
                  }}
                />
              )
        }
      
    </div>
    <SavedRecipientsCreateModal
      isOpen={showFilterCreateModal}
      onClose={() => {
          setShowFilterCreateModal(false);
          setFilterName(''); // Reset immediately
      }}
      title={"Create recipient filter"}
      subTitle={"Please assign a name to your recipient filter list."}
      filterName={filterName}
      setFilterName={setFilterName}
      confirmCreateClick={()=>{
          handleCreateRecipientFilter();
          setShowFilterCreateModal(false);
      }}
    />
    {/* Division List Overlay */}
      <DivisionListOverlay
        open={showDivisionOverlay}
        handleClose={handleCloseDivisionList}
        selectedFilter={selectedFilterForDivision}
        navMenu={overlayNavigation}
      />
      <RecipientFilterOverlay
        open={showRecipientFilterOverlay}
        handleClose={() => {
          console.log('handle close recipient filter overlay', selectedFilterForDivision)
          handlePrevious({filterName: selectedFilterForDivision?.name, rawFilter: selectedFilterForDivision?.filter, recipientCount: selectedFilterForDivision?.recipientCount});
          setShowRecipientFilterOverlay(false);
        }}
        onSuccessUpdate={( { filterName, rawFilter, recipientCount } ) => {
            handleSuccessUpdate({ filterName, rawFilter, recipientCount });
            refreshSavedFilters(true);
          }}
        mode={mode}
        navMenu={overlayNavigation}
      />
    </>
  );
}

function Recipient() {
  return (
    <CampaignProvider>
      <RecipientFilterProvider>
        <MessageDetailsFormProvider>
          <SavedFiltersManagementProvider>
            <RecipientContent />
          </SavedFiltersManagementProvider>
        </MessageDetailsFormProvider>
      </RecipientFilterProvider>
    </CampaignProvider>
  );
}

export default Recipient;
